import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import EmptyState from "@/components/feature/EmptyState";
import usePageMeta from "@/hooks/usePageMeta";
import { getRelatedSpecials, getSpecialById, getSpecialNews } from "@/utils/news";

export default function SpecialPage() {
  const { specialId = "" } = useParams();
  const special = getSpecialById(specialId);

  usePageMeta({
    title: special ? `${special.title} - 吃瓜爆料` : "专题 - 吃瓜爆料",
    description: special ? special.intro : "吃瓜爆料精选专题。",
    keywords: special ? `${special.title},新闻专题,吃瓜爆料` : "吃瓜爆料,专题",
  });

  if (!special) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="w-full px-4 md:px-8 py-8">
          <Breadcrumb items={[{ label: "专题" }]} />
          <EmptyState
            icon="ri-book-2-line"
            title="专题内容更新中，敬请期待"
            description="这个专题可能还在筹备，或者链接有误。你可以返回首页看看其他精彩内容。"
            action={
              <Link
                to="/"
                className="inline-flex items-center gap-1 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-home-4-line" /> 返回首页
              </Link>
            }
          />
        </main>
        <Footer />
      </div>
    );
  }

  const items = getSpecialNews(special);
  const related = getRelatedSpecials(special.id, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="w-full px-4 md:px-8 py-6">
        <Breadcrumb items={[{ label: "专题" }, { label: special.title }]} />

        <section className="relative w-full h-64 md:h-80 rounded-lg overflow-hidden mb-6">
          <img
            src={special.cover}
            alt={special.title}
            title={`${special.title} - 吃瓜爆料`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/10" />
          <div className="absolute bottom-0 left-0 p-6 md:p-10 w-full">
            <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium mb-3">
              <i className="ri-book-2-line" /> 特别策划
            </span>
            <h1 className="text-2xl md:text-4xl font-black text-background-50 leading-snug max-w-4xl">{special.title}</h1>
          </div>
        </section>

        <div className="rounded-lg bg-secondary-100 border border-secondary-200 p-5 md:p-7 mb-8">
          <h2 className="text-sm font-bold text-secondary-950 mb-2 flex items-center gap-2">
            <i className="ri-quill-pen-line text-primary-600" /> 专题导语
          </h2>
          <p className="text-sm md:text-base text-secondary-800 leading-relaxed max-w-4xl">{special.intro}</p>
        </div>

        <section className="mb-10">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-1.5 h-6 rounded-full bg-primary-500" />
            <h2 className="text-xl font-bold text-foreground-950">专题新闻集</h2>
            <span className="text-sm text-foreground-500">共 {items.length} 篇</span>
          </div>

          {items.length > 0 ? (
            <div className="flex flex-col gap-3">
              {items.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <EmptyState icon="ri-book-2-line" title="专题内容更新中，敬请期待" description="小编正在整理相关内容，很快与你见面。" />
          )}
        </section>

        {related.length > 0 && (
          <section>
            <div className="flex items-center gap-3 mb-5">
              <span className="w-1.5 h-6 rounded-full bg-accent-500" />
              <h2 className="text-xl font-bold text-foreground-950">相关专题推荐</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((s) => (
                <Link
                  key={s.id}
                  to={`/special/${s.id}`}
                  className="group rounded-lg overflow-hidden border border-background-200 bg-background-50 hover:border-primary-300 cursor-pointer transition-colors"
                >
                  <div className="relative w-full h-40 overflow-hidden bg-background-100">
                    <img
                      src={s.cover}
                      alt={s.title}
                      title={`${s.title} - 吃瓜爆料`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-base font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {s.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-2 leading-relaxed line-clamp-2">{s.intro}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}